import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { nearService } from '../services/near';
import toast from 'react-hot-toast';

const NearContext = createContext();

export const useNearContext = () => {
  const context = useContext(NearContext);
  if (!context) {
    throw new Error('useNearContext must be used within a NearProvider');
  }
  return context;
};

// Alias used across components
export const useNear = () => useContext(NearContext);

export const NearProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [balance, setBalance] = useState('0');
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null); // raw contract instance
  const [contractApi, setContractApi] = useState(null); // wrapper with helpers
  const previousAccountRef = useRef(null);
  const initAttemptedRef = useRef(false);

  // Initialize NEAR connection and update when wallet state changes
  useEffect(() => {
    if (initAttemptedRef.current) {
      return;
    }
    initAttemptedRef.current = true;

    const initNear = async () => {
      try {
        console.log('[NearBets] Initializing NEAR connection...');
        const signedIn = await nearService.initialize();
        const nearContract = nearService.contract || null;

        console.log('[NearBets] NEAR initialized:', { signedIn, hasContract: !!nearContract });

        setContractApi(nearContract);
        setContract(nearContract?.contract || null);
        setIsSignedIn(signedIn);

        const currentAccountId = nearService.getAccountId() || null;
        setAccountId(currentAccountId);

        if (signedIn && currentAccountId) {
          const accountBalance = await nearService.getAccountBalance();
          setBalance(accountBalance);
        } else {
          setBalance('0');
        }
        
        console.log('[NearBets] NEAR ready:', { accountId: currentAccountId, signedIn });
      } catch (error) {
        console.error('[NearBets] Failed to initialize NEAR:', error);
        toast.error('Failed to connect to NEAR network. Please refresh the page.');
        // Don't set contractApi if initialization failed
        setContractApi(null);
        setContract(null);
      } finally {
        setLoading(false);
      }
    };

    initNear();
  }, []);

  // Check wallet state after component mounts (catches wallet callback redirects)
  useEffect(() => {
    if (loading || !nearService.walletConnection) {
      return;
    }

    const checkWalletState = () => {
      const signedIn = nearService.isSignedIn();
      const currentAccountId = nearService.getAccountId();

      // Update state if wallet status changed
      if (signedIn !== isSignedIn || currentAccountId !== accountId) {
        console.log('[NearBets] Wallet state changed:', { 
          from: { signedIn: isSignedIn, accountId }, 
          to: { signedIn, accountId: currentAccountId } 
        });
        
        setIsSignedIn(signedIn);
        setAccountId(currentAccountId);

        if (signedIn && currentAccountId) {
          nearService.getAccountBalance().then(setBalance).catch(console.error);
        } else {
          setBalance('0');
        }
      }
    };

    // Check immediately
    checkWalletState();

    // Check periodically in case wallet state changes
    const interval = setInterval(checkWalletState, 1000);
    return () => clearInterval(interval);
  }, [loading, isSignedIn, accountId]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const previousAccount = previousAccountRef.current;
    if (previousAccount !== accountId) {
      previousAccountRef.current = accountId;
      queryClient.invalidateQueries();
    }
  }, [accountId, loading, queryClient]);

  // Sign in function
  const signIn = () => {
    try {
      nearService.signIn();
    } catch (error) {
      console.error('Sign in failed:', error);
      toast.error('Failed to sign in');
    }
  };

  // Sign out function
  const signOut = () => {
    try {
      // Clear all state immediately
      setIsSignedIn(false);
      setAccountId(null);
      setBalance('0');
      setContract(null);
      setContractApi(null);
      
      // Call NEAR service signOut (this will redirect)
      nearService.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      toast.error('Failed to sign out');
      // Force reload even if error occurs
      window.location.href = window.location.origin;
    }
  };

  // Refresh balance
  const refreshBalance = async () => {
    if (!isSignedIn) return;
    
    try {
      const accountBalance = await nearService.getAccountBalance();
      setBalance(accountBalance);
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    }
  };

  // Contract interaction helpers
  const executeTransaction = async (transactionFn, successMessage) => {
    if (!isSignedIn) {
      toast.error('Please sign in first');
      return null;
    }

    try {
      const result = await transactionFn();
      toast.success(successMessage);
      await refreshBalance(); // Refresh balance after transaction
      return result;
    } catch (error) {
      console.error('Transaction failed:', error);
      toast.error(error.message || 'Transaction failed');
      throw error;
    }
  };

  const formatNearAmount = (amount, fractionDigits = 2) => {
    try {
      const parsed = parseFloat(nearService.formatNearAmount(amount || '0'));
      if (Number.isNaN(parsed)) {
        return '0';
      }
      return parsed.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    } catch (err) {
      console.error('Failed to format NEAR amount:', err);
      return '0';
    }
  };

  const value = {
    // State
    isSignedIn,
    accountId,
    balance,
    loading,
    contract,
    contractApi,

    // Services
    nearService,

    // Actions
    signIn,
    signOut,
    refreshBalance,
    executeTransaction,

    // Utilities
    formatNearAmount,
    parseNearAmount: nearService.parseNearAmount,
  };

  return (
    <NearContext.Provider value={value}>
      {children}
    </NearContext.Provider>
  );
};

export default NearContext;
